import { FC, createContext, useContext, useState, useEffect } from 'react'
import { DefaultConfig } from './_LayoutConfig'
import {
  setLayoutIntoLocalStorage,
  getEmptyCssClasses,
  getEmptyCSSVariables,
  getEmptyHTMLAttributes,
  LayoutSetup,
} from './_LayoutSetup'
import {
  ILayout,
  ILayoutCSSVariables,
  ILayoutCSSClasses,
  ILayoutHTMLAttributes,
  LayoutType,
  ToolbarType,
} from './_Models'
import { WithChildren } from '../../helpers'
import CommonLoader from '../../partials/widgets/CommonLoader'

export interface LayoutContextModel {
  config: ILayout
  classes: ILayoutCSSClasses
  attributes: ILayoutHTMLAttributes
  cssVariables: ILayoutCSSVariables
  setLayout: (config: LayoutSetup) => void
  setLayoutType: (layoutType: LayoutType) => void
  setToolbarType: (toolbarType: ToolbarType) => void
  rightToolbar?: any
  setRightTolbar?: any
  currentTicket?: any
  setCurrentTicket?: any
  manageTemplateDateFilter?: any
  setManageTemplateDateFilter?: any
  manageTemplatePeidFilter?: any
  setManageTemplatePeidFilter?: any
  uuidOfVendorForIpList?: any
  setUuidOfVendorForIpList?: any
  uuidOfURLApproval?: any,
  setUuidOfURLApproval?: any,
  urlApprovalItem?: any,
  setUrlApprovalItem?: any,
  peIdSmsReport?: any
  setPeIdSmsReport?: any
  dateFilterSmsReport?: any
  setDateFilterSmsReport?: any
  dateFilterDashboard?: any
  setDateFilterDashboard?: any
  searchQuery?: any
  setSearchQuery?: any
  statusFilter?: any
  setStatusFilter?: any
  showLoader: () => void
  hideLoader: () => void
}

const LayoutContext = createContext<LayoutContextModel>({
  config: DefaultConfig,
  classes: getEmptyCssClasses(),
  attributes: getEmptyHTMLAttributes(),
  cssVariables: getEmptyCSSVariables(),
  setLayout: (config: LayoutSetup) => { },
  setLayoutType: (layoutType: LayoutType) => { },
  setToolbarType: (toolbarType: ToolbarType) => { },
  rightToolbar: '',
  setRightTolbar: () => { },
  currentTicket: '',
  setCurrentTicket: () => { },
  manageTemplateDateFilter: [],
  setManageTemplateDateFilter: () => { },
  manageTemplatePeidFilter: [],
  setManageTemplatePeidFilter: () => { },
  uuidOfVendorForIpList: '',
  setUuidOfVendorForIpList: () => { },
  uuidOfURLApproval: '',
  setUuidOfURLApproval: () => { },
  urlApprovalItem: null,
  setUrlApprovalItem: () => { },
  peIdSmsReport: '',
  setPeIdSmsReport: () => { },
  dateFilterDashboard: [],
  setDateFilterDashboard: () => { },
  setStatusFilter: () => { },
  setSearchQuery: () => { },
  showLoader: () => { },
  hideLoader: () => { }
})

const enableSplashScreen = () => {
  const splashScreen = document.getElementById('splash-screen')
  if (splashScreen) {
    splashScreen.style.setProperty('display', 'flex')
  }
}

const disableSplashScreen = () => {
  const splashScreen = document.getElementById('splash-screen')
  if (splashScreen) {
    splashScreen.style.setProperty('display', 'none')
  }
}

const LayoutProvider: FC<WithChildren> = ({ children }) => {
  const [config, setConfig] = useState(LayoutSetup.config)
  const [classes, setClasses] = useState(LayoutSetup.classes)
  const [attributes, setAttributes] = useState(LayoutSetup.attributes)
  const [cssVariables, setCSSVariables] = useState(LayoutSetup.cssVariables)
  const [rightToolbar, setRightTolbar] = useState(<></>)
  const [currentTicket, setCurrentTicket] = useState({})
  const [manageTemplateDateFilter, setManageTemplateDateFilter] = useState([])
  const [manageTemplatePeidFilter, setManageTemplatePeidFilter] = useState('')
  const [uuidOfVendorForIpList, setUuidOfVendorForIpList] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [uuidOfURLApproval, setUuidOfURLApproval] = useState('')
  const [urlApprovalItem, setUrlApprovalItem] = useState<any>(null)
  const [peIdSmsReport, setPeIdSmsReport] = useState('')
  const [dateFilterSmsReport, setDateFilterSmsReport] = useState([])
  const [dateFilterDashboard, setDateFilterDashboard] = useState(['', ''])
  const [loading, setLoading] = useState(false)

  const showLoader = () => {
    setLoading(true)
  }
  const hideLoader = () => {
    setLoading(false)
  }

  const setLayout = (_themeConfig: Partial<ILayout>) => {
    enableSplashScreen()
    const bodyClasses = Array.from(document.body.classList)
    bodyClasses.forEach((cl) => document.body.classList.remove(cl))
    const updatedConfig = LayoutSetup.updatePartialConfig(_themeConfig)
    setConfig(Object.assign({}, updatedConfig))
    setClasses(LayoutSetup.classes)
    setAttributes(LayoutSetup.attributes)
    setCSSVariables(LayoutSetup.cssVariables)
    setTimeout(() => {
      disableSplashScreen()
    }, 500)
  }

  const setToolbarType = (toolbarType: ToolbarType) => {
    const updatedConfig = { ...config }
    if (updatedConfig.app?.toolbar) {
      updatedConfig.app.toolbar.layout = toolbarType
    }

    setLayoutIntoLocalStorage(updatedConfig)
    window.location.reload()
  }

  const setLayoutType = (layoutType: LayoutType) => {
    const updatedLayout = { ...config, layoutType }
    setLayoutIntoLocalStorage(updatedLayout)
    setConfig(updatedLayout)
  }

  const value: LayoutContextModel = {
    config,
    classes,
    attributes,
    cssVariables,
    setLayout,
    setLayoutType,
    setToolbarType,
    rightToolbar,
    setRightTolbar,
    currentTicket,
    setCurrentTicket,
    manageTemplateDateFilter,
    setManageTemplateDateFilter,
    manageTemplatePeidFilter,
    setManageTemplatePeidFilter,
    uuidOfVendorForIpList,
    setUuidOfVendorForIpList,
    uuidOfURLApproval,
    setUuidOfURLApproval,
    urlApprovalItem,
    setUrlApprovalItem,
    peIdSmsReport,
    setPeIdSmsReport,
    dateFilterSmsReport,
    setDateFilterSmsReport,
    dateFilterDashboard,
    setDateFilterDashboard,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    showLoader,
    hideLoader
  }

  useEffect(() => {
    disableSplashScreen()
  }, [])

  return <LayoutContext.Provider value={value}>{children}{loading && <CommonLoader />}</LayoutContext.Provider>
}

export { LayoutContext, LayoutProvider }

export function useLayout() {
  return useContext(LayoutContext)
}
